import { fixedUi } from '@/i18n/fixedUi';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import i18n from '@/i18n';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Plus } from 'lucide-react';
import TiktokOpsPager, { TIKTOK_OPS_DEFAULT_PAGE_SIZE } from '@/tiktok-platform/components/TiktokOpsPager';
import { togglePageSelection } from '@/tiktok-platform/utils/tiktokPageSelection';
import TiktokDateRangeFilter, {
  pickDatePart,
  toDayEndDateTime,
  toDayStartDateTime,
  validateDateRange,
} from '@/tiktok-platform/components/TiktokDateRangeFilter';
import TiktokSyncRangeModal from '@/tiktok-platform/components/TiktokSyncRangeModal';
import BatchQueryModal from '@/tiktok-platform/modules/products/components/BatchQueryModal';
import {
  BATCH_QUERY_LIMIT,
  joinBatchTokens,
  parseBatchTokens,
} from '@/tiktok-platform/modules/products/components/batchQueryUtils';
import { ProductFilterCard, ProductListCard } from '@/tiktok-platform/modules/products/components/ProductPageCards';
import TiktokOpsListToolbar, { downloadCsv } from '@/tiktok-platform/components/TiktokOpsListToolbar';
import TiktokSyncProgressSlot from '@/tiktok-platform/components/TiktokSyncProgressSlot';
import { buildOpsExportMenu, opsExportLabel } from '@/tiktok-platform/utils/buildOpsExportMenu';
import HoverCopyField from '@/tiktok-platform/modules/products/components/HoverCopyField';
import TiktokProductThumb from '@/tiktok-platform/modules/products/components/TiktokProductThumb';
import { showTiktokConfirm } from '@/tiktok-platform/components/TiktokConfirmDialog';
import { useGuardedRefresh } from '@/tiktok-platform/hooks/useGuardedRefresh';
import { useTiktokSyncJobs } from '@/tiktok-platform/context/TiktokSyncJobContext';
import { useTiktokPageShopScope, stackShopDimensionTabs } from '@/tiktok-platform/hooks/useTiktokPageShopScope';
import {
  SALES_ORDER_STATUS_TABS,
} from '@/tiktok-platform/constants/tiktokApiCatalog';
import { buildTiktokPath } from '@/tiktok-platform/constants/tiktokRoutes';
import {
  formatTrackTime,
  listDateRows,
  listLogisticsSummary,
  listOrderInfoRows,
  listOrderProductSummary,
  tiktokOrderActions,
  advanceOrderStatus,
  listPrimaryPrices,
  salesStatusLabel,
  trackInfoList,
} from '@/tiktok-platform/modules/aftersales/salesOrderDisplay';
import { tiktokPlatformAPI } from '@/tiktok-platform/api/tiktokPlatformApi';
import { extractTiktokList, extractTiktokTotal, pickField } from '@/tiktok-platform/utils/tiktokResponse';
import { formatSiteListSyncedAt } from '@/tiktok-platform/utils/tiktokSiteList';
import { showToast } from '@/utils/toast';
import { readNoticeOrderNo } from '@/tiktok-platform/utils/tiktokNoticeFocusQuery';
import '@/styles/tiktok-platform/tiktok-product-ops.css';

const ORDER_ID_OPTIONS = [
  { value: 'orderNo', labelKey: 'orderList.orderNo' },
  { value: 'waybillNo', labelKey: 'orderList.waybillNo' },
];

const EMPTY_FILTER = {
  orderIdType: 'orderNo',
  orderIdValue: '',
  skc: '',
  orderStatus: '',
  startTime: '',
  endTime: '',
};

function rowKey(row, index) {
  return String(pickField(row, 'orderNo', 'orderNumber') || `row-${index}`);
}

function collectPackageIds(detail) {
  const ids = [];
  const push = (value) => {
    const id = String(value || '').trim();
    if (id && !ids.includes(id)) ids.push(id);
  };
  [detail?.packages, detail?.package_list, detail?.packageWaybillList].forEach((list) => {
    if (!Array.isArray(list)) return;
    list.forEach((row) => push(row?.id || row?.package_id || row?.packageId || row?.packageNo));
  });
  if (Array.isArray(detail?.line_items)) {
    detail.line_items.forEach((item) => push(item?.package_id || item?.packageId));
  }
  return ids;
}

function validateOrderWindow(startDate, endDate) {
  const basic = validateDateRange(startDate, endDate);
  if (basic) return basic;
  const start = pickDatePart(startDate);
  const end = pickDatePart(endDate);
  if (!start || !end) return '';
  const ms = new Date(`${end}T00:00:00`).getTime() - new Date(`${start}T00:00:00`).getTime();
  if (ms > 7 * 24 * 60 * 60 * 1000) {
    return i18n.t('orderList.window7', { ns: 'workbench' });
  }
  return '';
}

function statusModifier(code) {
  const s = String(code || '').toUpperCase();
  if (s === 'COMPLETED' || s === 'DELIVERED' || s === '5') return 'success';
  if (s === 'CANCELLED' || s === 'CANCELED' || s === '6' || s === '8' || s === '9') return 'failed';
  return 'pending';
}

/**
 * 订单列表：展示产品 / 订单 / 物流 / 状态 / 日期 / 价格。
 * 同步走 sync-jobs(module=order)；轨迹走 Get Logistics Tracking（按订单号，只读）。
 */
export default function OrderListPage() {
  const { t } = useTranslation('workbench');
  const statusText = (code) => {
    const raw = salesStatusLabel(code);
    const key = ({
      '全部': 'tabAll',
      '待付款': 'tabUnpaid',
      '保留中': 'tabOnHold',
      '待发货': 'tabAwaitShip',
      '部分发货': 'tabPartialShip',
      '待揽收': 'tabAwaitCollect',
      '运输中': 'tabInTransit',
      '已送达': 'tabDelivered',
      '已完成': 'tabCompleted',
      '已取消': 'tabCancelled',
      '待处理': 'tabPending',
      '待TEMU发货': 'tabTemuShip',
      '待SHEIN发货': 'tabSheinShip',
      '已发货': 'tabShipped',
      '已签收': 'tabSigned',
      '用户已退款': 'tabUserRefund',
      '已报损': 'tabDamaged',
      '已拒收': 'tabRejected',
      '已退款': 'tabRefunded',
    })[raw];
    return key ? t(`orderList.${key}`) : raw;
  };
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const {
    loading: shopLoading,
    shopDimensionTabs,
    concreteScopeId,
    allShopsScope,
  } = useTiktokPageShopScope();
  // 单店 API：页内「全部」时 concreteScopeId 为空，需先选具体店（tabs 仍可见）
  const activeShopId = concreteScopeId;
  const { startJob, onTrackDone, isBusy } = useTiktokSyncJobs();
  const pendingSyncRangeRef = useRef(null);

  const [filterDraft, setFilterDraft] = useState(() => ({ ...EMPTY_FILTER }));
  const [query, setQuery] = useState(() => ({ ...EMPTY_FILTER, _nonce: 0 }));
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(TIKTOK_OPS_DEFAULT_PAGE_SIZE);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState('');
  const [batchModalOpen, setBatchModalOpen] = useState(false);
  const [batchModalField, setBatchModalField] = useState(null); // orderIdValue | skc
  const [syncRangeOpen, setSyncRangeOpen] = useState(false);
  const [lastSyncedAt, setLastSyncedAt] = useState('');
  const [persistedSyncedAt, setPersistedSyncedAt] = useState('');
  const [trackPanel, setTrackPanel] = useState(null);
  const [statusCounts, setStatusCounts] = useState(() => ({}));
  const [selectedKeys, setSelectedKeys] = useState(() => new Set());
  const [deleting, setDeleting] = useState(false);
  const selectedRowsRef = useRef(new Map());

  useEffect(() => {
    setPage(1);
    setRows([]);
    setTotal(0);
    setError('');
    setLastSyncedAt('');
    setPersistedSyncedAt('');
    setTrackPanel(null);
    setStatusCounts({});
    setSelectedKeys(new Set());
    selectedRowsRef.current.clear();
  }, [activeShopId]);

  const noticeOrderNo = readNoticeOrderNo(searchParams);
  useEffect(() => {
    if (!noticeOrderNo) return;
    const next = {
      ...EMPTY_FILTER,
      orderIdType: 'orderNo',
      orderIdValue: noticeOrderNo,
    };
    setFilterDraft(next);
    setQuery((prev) => ({ ...next, _nonce: (prev._nonce || 0) + 1 }));
    setPage(1);
  }, [noticeOrderNo, activeShopId]);

  useEffect(() => {
    if (!activeShopId) {
      setPersistedSyncedAt('');
      return undefined;
    }
    let cancelled = false;
    tiktokPlatformAPI.getShopSyncStatus(activeShopId)
      .then((res) => {
        if (cancelled) return;
        const modules = Array.isArray(res?.modules) ? res.modules : [];
        const mod = modules.find((m) => String(m.module || '').toUpperCase() === 'ORDER');
        const at = mod?.lastSyncEndAt || mod?.lastRunAt || '';
        setPersistedSyncedAt(at ? String(at) : '');
      })
      .catch(() => {
        if (!cancelled) setPersistedSyncedAt('');
      });
    return () => { cancelled = true; };
  }, [activeShopId]);

  const queryParams = useMemo(() => {
    const statusRaw = query.orderStatus === '' || query.orderStatus == null
      ? null
      : String(query.orderStatus);
    const startDate = pickDatePart(query.startTime);
    const endDate = pickDatePart(query.endTime);
    const idType = query.orderIdType === 'waybillNo' ? 'waybillNo' : 'orderNo';
    const idValue = query.orderIdValue || undefined;
    return {
      page,
      pageSize,
      source: 'cached',
      orderStatus: statusRaw || undefined,
      orderNo: idType === 'orderNo' ? idValue : undefined,
      waybillNo: idType === 'waybillNo' ? idValue : undefined,
      skc: query.skc || undefined,
      startTime: toDayStartDateTime(startDate) || undefined,
      endTime: toDayEndDateTime(endDate) || undefined,
      _nonce: query._nonce,
    };
  }, [query, page, pageSize]);

  const load = useCallback(async () => {
    if (!activeShopId) {
      setRows([]);
      setTotal(0);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError('');
    try {
      const { _nonce, ...params } = queryParams;
      const res = await tiktokPlatformAPI.listTiktokOrders(activeShopId, params);
      const list = extractTiktokList(res);
      setRows(list);
      setTotal(extractTiktokTotal(res, list.length));
      const counts = res?.statusCounts && typeof res.statusCounts === 'object' ? res.statusCounts : {};
      setStatusCounts(counts);
    } catch (e) {
      setRows([]);
      setTotal(0);
      setStatusCounts({});
      setError(e?.message || t('orderList.loadFailed'));
    } finally {
      setLoading(false);
    }
  }, [activeShopId, queryParams, t]);

  useEffect(() => {
    load();
  }, [load]);

  const handleSync = useCallback(async () => {
    const range = pendingSyncRangeRef.current;
    if (!activeShopId || !range?.startTime || !range?.endTime) return;
    try {
      const track = await startJob({
        kind: 'orders',
        shopId: activeShopId,
        label: t('orderList.syncUpdate'),
        payload: {
          startTime: range.startTime,
          endTime: range.endTime,
        },
      });
      if (!track?.id) return;
      const result = await new Promise((resolve) => {
        const unsub = onTrackDone(track.id, (r) => {
          unsub();
          resolve(r);
        });
      });
      if (!result?.ok) return;
      const finishedAt = new Date().toISOString();
      setLastSyncedAt(finishedAt);
      setPersistedSyncedAt(finishedAt);
      setQuery((prev) => ({
        ...prev,
        startTime: pickDatePart(range.startTime) || prev.startTime,
        endTime: pickDatePart(range.endTime) || prev.endTime,
        _nonce: prev._nonce + 1,
      }));
      setFilterDraft((prev) => ({
        ...prev,
        startTime: pickDatePart(range.startTime) || prev.startTime,
        endTime: pickDatePart(range.endTime) || prev.endTime,
      }));
      setPage(1);
      setSyncRangeOpen(false);
      pendingSyncRangeRef.current = null;
    } catch (e) {
      if (e?.code === 'SYNC_IN_PROGRESS' || e?.code === 'ABORT') {
        return;
      }
      showToast(e?.message || t('orderList.syncFailed'), 'error');
    }
  }, [activeShopId, startJob, onTrackDone, t]);

  const {
    refresh: guardedSync,
    refreshing: syncingLocal,
    cooldownSeconds: syncCooldown,
  } = useGuardedRefresh(handleSync);

  const syncing = syncingLocal || isBusy('orders', activeShopId);

  const updateField = (key, value) => {
    setFilterDraft((prev) => ({ ...prev, [key]: value }));
  };

  const onQuery = () => {
    const orderTokens = parseBatchTokens(filterDraft.orderIdValue).slice(0, BATCH_QUERY_LIMIT);
    const skcTokens = parseBatchTokens(filterDraft.skc).slice(0, BATCH_QUERY_LIMIT);
    const start = pickDatePart(filterDraft.startTime);
    const end = pickDatePart(filterDraft.endTime);
    const rangeError = validateDateRange(start, end);
    if (rangeError) {
      showToast(rangeError, 'error');
      return;
    }
    const windowErr = validateOrderWindow(start, end);
    if (windowErr) {
      showToast(windowErr, 'warning');
      return;
    }
    if (activeShopId) setLoading(true);
    setPage(1);
    setTrackPanel(null);
    setQuery((prev) => ({
      orderIdType: filterDraft.orderIdType === 'waybillNo' ? 'waybillNo' : 'orderNo',
      orderIdValue: orderTokens.join(','),
      skc: skcTokens.join(','),
      orderStatus: prev.orderStatus || '',
      startTime: start,
      endTime: end,
      _nonce: prev._nonce + 1,
    }));
  };

  const onReset = () => {
    setFilterDraft((prev) => ({
      ...EMPTY_FILTER,
      orderStatus: prev.orderStatus || '',
    }));
    setPage(1);
    setTrackPanel(null);
    setQuery((prev) => ({
      ...EMPTY_FILTER,
      orderStatus: prev.orderStatus || '',
      _nonce: prev._nonce + 1,
    }));
  };

  const onStatusTab = useCallback((value) => {
    const nextStatus = value == null ? '' : String(value);
    setFilterDraft((prev) => ({ ...prev, orderStatus: nextStatus }));
    setPage(1);
    setTrackPanel(null);
    setSelectedKeys(new Set());
    selectedRowsRef.current.clear();
    setQuery((prev) => ({
      ...prev,
      orderStatus: nextStatus,
      _nonce: prev._nonce + 1,
    }));
  }, []);

  const statusTabs = useMemo(() => (
    <div className="tko-status-tabs" role="tablist" aria-label={t('orderList.tabsAria')}>
      {SALES_ORDER_STATUS_TABS.map((tab) => {
        const countKey = tab.value === '' ? 'all' : String(tab.value);
        const count = Number(statusCounts?.[countKey] ?? 0);
        const active = String(query.orderStatus || '') === String(tab.value);
        return (
          <button
            key={tab.value || 'all'}
            type="button"
            role="tab"
            aria-selected={active}
            className={`tko-status-tab${active ? ' tko-status-tab--active' : ''}`}
            onClick={() => onStatusTab(tab.value)}
          >
            {t(`orderList.${({
              '全部': 'tabAll',
              '待付款': 'tabUnpaid',
              '保留中': 'tabOnHold',
              '待发货': 'tabAwaitShip',
              '部分发货': 'tabPartialShip',
              '待揽收': 'tabAwaitCollect',
              '运输中': 'tabInTransit',
              '已送达': 'tabDelivered',
              '已完成': 'tabCompleted',
              '已取消': 'tabCancelled',
              '待处理': 'tabPending',
              '待TEMU发货': 'tabTemuShip',
              '待SHEIN发货': 'tabSheinShip',
              '已发货': 'tabShipped',
              '已签收': 'tabSigned',
              '用户已退款': 'tabUserRefund',
              '已报损': 'tabDamaged',
              '已拒收': 'tabRejected',
              '已退款': 'tabRefunded',
            })[tab.label] || 'tabAll'}`, { defaultValue: tab.label })}
            <span className="tko-status-tab__count">{Number.isFinite(count) ? count : 0}</span>
          </button>
        );
      })}
    </div>
  ), [query.orderStatus, statusCounts, onStatusTab, t]);

  const listTabs = useMemo(
    () => stackShopDimensionTabs(shopDimensionTabs, statusTabs),
    [shopDimensionTabs, statusTabs],
  );

  const selectableKeys = useMemo(
    () => rows.map((row, index) => rowKey(row, index)).filter(Boolean),
    [rows],
  );
  const allSelected = selectableKeys.length > 0
    && selectableKeys.every((key) => selectedKeys.has(key));

  const toggleAll = () => {
    setSelectedKeys((prev) => togglePageSelection(prev, selectableKeys, allSelected));
  };

  const toggleOne = (key, row) => {
    setSelectedKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
        selectedRowsRef.current.delete(key);
      } else {
        next.add(key);
        if (row) selectedRowsRef.current.set(key, row);
      }
      return next;
    });
  };

  useEffect(() => {
    rows.forEach((row, index) => {
      const key = rowKey(row, index);
      if (selectedKeys.has(key)) selectedRowsRef.current.set(key, row);
    });
  }, [rows, selectedKeys]);

  const selectedRows = useMemo(() => {
    const map = new Map(selectedRowsRef.current);
    selectableKeys.forEach((key) => {
      if (!selectedKeys.has(key)) map.delete(key);
    });
    rows.forEach((row, index) => {
      const key = rowKey(row, index);
      if (selectedKeys.has(key)) map.set(key, row);
    });
    return Array.from(selectedKeys)
      .map((key) => map.get(key))
      .filter(Boolean);
  }, [rows, selectedKeys, selectableKeys]);

  const openDetail = (orderNo) => {
    if (!orderNo) return;
    navigate(buildTiktokPath(`orders/${encodeURIComponent(orderNo)}`));
  };

  const [processingNo, setProcessingNo] = useState('');
  const [waybillModal, setWaybillModal] = useState(null);

  const resolvePackageId = async (row, orderNo) => {
    const localId = collectPackageIds(row)[0];
    if (localId) return localId;
    const live = await tiktokPlatformAPI.fetchTiktokOrderDetails(activeShopId, {
      orderNoList: [orderNo],
    });
    const list = extractTiktokList(live);
    const record = (live && typeof live.order === 'object' ? live.order : null)
      || list.find((item) => String(pickField(item, 'orderNo', 'orderNumber', 'id')) === String(orderNo))
      || list[0]
      || null;
    return collectPackageIds(record)[0] || '';
  };

  const handleProcess = async (row, orderNo) => {
    if (!activeShopId || !orderNo || processingNo) return;
    const ok = await showTiktokConfirm({
      title: t('orderList.processTitle'),
      message: t('orderList.processConfirm', { no: orderNo }),
      tone: 'warning',
      confirmText: t('orderList.process'),
      cancelText: t('shop.cancel'),
    });
    if (!ok) return;
    setProcessingNo(orderNo);
    try {
      const packageId = await resolvePackageId(row, orderNo);
      if (!packageId) {
        showToast(t('orderList.noPackage'), 'warning');
        return;
      }
      await tiktokPlatformAPI.shipTiktokPackage(activeShopId, packageId, {
        handover_method: 'PICKUP',
        order_id: orderNo,
      });
      showToast(t('orderList.shipSubmitted'), 'success');
      setRows((prev) => prev.map((item) => (
        String(pickField(item, 'orderNo', 'orderNumber') || '') === String(orderNo)
          ? advanceOrderStatus(item, 'AWAITING_COLLECTION')
          : item
      )));
      load();
    } catch (e) {
      showToast(e?.message || t('orderList.shipFailed'), 'error');
    } finally {
      setProcessingNo('');
    }
  };

  const deliveryOptionIdOf = (row) => {
    const payload = row?.payload && typeof row.payload === 'object' ? row.payload : row;
    const line = Array.isArray(payload?.line_items) ? payload.line_items[0] : null;
    const pkg = Array.isArray(payload?.packages) ? payload.packages[0] : null;
    return String(
      payload?.delivery_option_id
      || line?.delivery_option_id
      || pkg?.delivery_option_id
      || '',
    ).trim();
  };

  const printLabel = async (row, orderNo) => {
    if (!activeShopId || !orderNo || processingNo) return;
    setProcessingNo(orderNo);
    try {
      const packageId = await resolvePackageId(row, orderNo);
      if (!packageId) {
        showToast(t('orderList.noPackage'), 'warning');
        return;
      }
      const res = await tiktokPlatformAPI.getTiktokShippingDocument(activeShopId, packageId);
      const data = res?.data && typeof res.data === 'object' ? res.data : res;
      const url = data?.doc_url || data?.docUrl || data?.url || '';
      if (!url) {
        showToast(t('orderList.noLabelUrl'), 'warning');
        return;
      }
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch (e) {
      showToast(e?.message || t('orderList.labelFailed'), 'error');
    } finally {
      setProcessingNo('');
    }
  };

  const openWaybill = async (row, orderNo) => {
    if (!orderNo) return;
    const deliveryOptionId = deliveryOptionIdOf(row);
    setWaybillModal({
      orderNo,
      providerId: '',
      trackingNo: '',
      providers: [],
      loadingProviders: Boolean(activeShopId && deliveryOptionId),
      submitting: false,
    });
    if (!activeShopId || !deliveryOptionId) return;
    try {
      const res = await tiktokPlatformAPI.listShippingProviders(activeShopId, deliveryOptionId);
      const data = res?.data && typeof res.data === 'object' ? res.data : res;
      const providers = Array.isArray(data?.shipping_providers) ? data.shipping_providers : [];
      setWaybillModal((prev) => (prev && prev.orderNo === orderNo
        ? { ...prev, providers, loadingProviders: false }
        : prev));
    } catch {
      setWaybillModal((prev) => (prev && prev.orderNo === orderNo
        ? { ...prev, providers: [], loadingProviders: false }
        : prev));
    }
  };

  const submitWaybill = async () => {
    if (!waybillModal || !activeShopId || waybillModal.submitting) return;
    const trackingNo = String(waybillModal.trackingNo || '').trim();
    const providerId = String(waybillModal.providerId || '').trim();
    if (!trackingNo || !providerId) {
      showToast(fixedUi("请填写物流商和运单号"), 'warning');
      return;
    }
    setWaybillModal((prev) => (prev ? { ...prev, submitting: true } : prev));
    try {
      await tiktokPlatformAPI.markTiktokOrderShipped(activeShopId, waybillModal.orderNo, {
        tracking_number: trackingNo,
        shipping_provider_id: providerId,
      });
      showToast(fixedUi("运单已提交"), 'success');
      const shippedNo = waybillModal.orderNo;
      setRows((prev) => prev.map((item) => (
        String(pickField(item, 'orderNo', 'orderNumber') || '') === String(shippedNo)
          ? advanceOrderStatus(item, 'IN_TRANSIT')
          : item
      )));
      setWaybillModal(null);
      load();
    } catch (e) {
      showToast(e?.message || fixedUi('上传运单失败'), 'error');
      setWaybillModal((prev) => (prev ? { ...prev, submitting: false } : prev));
    }
  };

  const openTrack = (row, packageIdx = 0) => {
    const key = rowKey(row, 0);
    const logistics = listLogisticsSummary(row);
    const items = logistics.items.length
      ? logistics.items
      : [{ packageNo: '', waybillNo: '', carrier: '', trackSnap: null }];
    const idx = Math.min(Math.max(0, packageIdx), items.length - 1);
    setTrackPanel({
      key,
      packageIdx: idx,
      items,
      liveLoading: false,
      liveError: '',
      liveTrack: null,
    });
  };

  const refreshLiveTrack = async (row) => {
    if (!activeShopId || !trackPanel) return;
    const item = trackPanel.items[trackPanel.packageIdx];
    const orderNo = String(pickField(row, 'orderNo', 'orderNumber') || '').trim();
    if (!orderNo) return;
    setTrackPanel((prev) => (prev ? { ...prev, liveLoading: true, liveError: '', liveTrack: null } : prev));
    try {
      const data = await tiktokPlatformAPI.getTiktokOrderLogisticsTrack(activeShopId, {
        orderNo,
        waybillNo: item?.waybillNo,
      });
      setTrackPanel((prev) => (prev ? { ...prev, liveLoading: false, liveTrack: data } : prev));
    } catch (e) {
      const raw = String(e?.message || '');
      const missing = /9999002|数据不存在|not\s*found|no\s*data/i.test(raw);
      const message = missing ? t('orderList.noTrackData') : t('orderList.noTrack');
      setTrackPanel((prev) => (prev ? { ...prev, liveLoading: false, liveError: message } : prev));
      if (!missing) showToast(message, 'warning');
    }
  };

  const latestSyncedAt = lastSyncedAt || persistedSyncedAt;
  const latestSyncedLabel = latestSyncedAt
    ? t('orderList.lastUpdate', { time: formatSiteListSyncedAt(latestSyncedAt) })
    : '';

  const emptyTitle = !activeShopId
    ? (allShopsScope ? t('orderList.pickConcrete') : t('orderList.pickAuthorizedShop'))
    : loading
      ? t('orderList.querying')
      : t('orderList.empty');
  const emptyDesc = !activeShopId
    ? (allShopsScope
      ? t('orderList.emptyAllShops')
      : t('orderList.emptyNeedShop'))
    : loading
      ? ''
      : t('orderList.emptyPull', { platform: 'TikTok' });

  const exportPage = () => {
    if (!rows.length) {
      showToast(t('orderList.noneToExport'), 'warning');
      return;
    }
    const headers = [t('orderList.orderNo'), t('orderList.status'), t('orderList.site'), t('orderList.ordered'), t('orderList.paid'), t('orderList.signed'), t('orderList.income'), t('orderList.product'), t('orderList.waybillNo')];
    const csvRows = rows.map((row) => {
      const product = listOrderProductSummary(row);
      const dates = listDateRows(row);
      const prices = listPrimaryPrices(row);
      const logistics = listLogisticsSummary(row);
      return [
        pickField(row, 'orderNo', 'orderNumber') || '',
        statusText(pickField(row, 'orderStatus')),
        pickField(row, 'salesSite', 'site') || '',
        dates[0]?.value || '',
        dates[1]?.value || '',
        dates[2]?.value || '',
        prices.primary[0]?.value || '',
        product.title || '',
        logistics.items.map((i) => i.waybillNo || i.packageNo).filter(Boolean).join('|'),
      ];
    });
    downloadCsv(`tiktok-orders-${Date.now()}.csv`, headers, csvRows);
    showToast(t('orderList.exported', { count: csvRows.length }), 'success');
  };

  const exportFiltered = async () => {
    if (!activeShopId) {
      showToast(t('orderList.pickShop'), 'warning');
      return;
    }
    if (total <= 0 && rows.length === 0) {
      showToast(t('orderList.noneToExport'), 'warning');
      return;
    }
    setExporting(true);
    try {
      const { _nonce, ...baseParams } = queryParams;
      const size = 100;
      let pageNo = 1;
      let expectedTotal = Number.isFinite(total) && total > 0 ? total : Infinity;
      const all = [];
      while (all.length < expectedTotal) {
        const res = await tiktokPlatformAPI.listTiktokOrders(activeShopId, {
          ...baseParams,
          page: pageNo,
          pageSize: size,
        });
        const list = extractTiktokList(res);
        expectedTotal = extractTiktokTotal(res, list.length);
        all.push(...list);
        if (!list.length || list.length < size) break;
        pageNo += 1;
        if (pageNo > 500) break;
      }
      setRows((prev) => (page === 1 ? prev : prev));
      const headers = [t('orderList.orderNo'), t('orderList.status'), t('orderList.site'), t('orderList.ordered'), t('orderList.paid'), t('orderList.signed'), t('orderList.income'), t('orderList.product')];
      const csvRows = all.map((row) => {
        const product = listOrderProductSummary(row);
        const dates = listDateRows(row);
        const prices = listPrimaryPrices(row);
        return [
          pickField(row, 'orderNo', 'orderNumber') || '',
          statusText(pickField(row, 'orderStatus')),
          pickField(row, 'salesSite', 'site') || '',
          dates[0]?.value || '',
          dates[1]?.value || '',
          dates[2]?.value || '',
          prices.primary[0]?.value || '',
          product.title || '',
        ];
      });
      if (!csvRows.length) {
        showToast(t('orderList.noneToExport'), 'warning');
        return;
      }
      downloadCsv(`tiktok-orders-filtered-${Date.now()}.csv`, headers, csvRows);
      showToast(t('orderList.exported', { count: csvRows.length }), 'success');
    } catch (e) {
      showToast(e?.message || t('orderList.exportFilterFailed'), 'error');
    } finally {
      setExporting(false);
    }
  };

  const exportSelected = () => {
    if (!selectedRows.length) {
      showToast(t('orderList.pickToExport'), 'warning');
      return;
    }
    const headers = [t('orderList.orderNo'), t('orderList.status'), t('orderList.site'), t('orderList.ordered'), t('orderList.paid'), t('orderList.signed'), t('orderList.income'), t('orderList.product'), t('orderList.waybillNo')];
    const csvRows = selectedRows.map((row) => {
      const product = listOrderProductSummary(row);
      const dates = listDateRows(row);
      const prices = listPrimaryPrices(row);
      const logistics = listLogisticsSummary(row);
      return [
        pickField(row, 'orderNo', 'orderNumber') || '',
        statusText(pickField(row, 'orderStatus')),
        pickField(row, 'salesSite', 'site') || '',
        dates[0]?.value || '',
        dates[1]?.value || '',
        dates[2]?.value || '',
        prices.primary[0]?.value || product.incomeText || '',
        product.title || '',
        logistics.items.map((i) => i.waybillNo || i.packageNo).filter(Boolean).join('|'),
      ];
    });
    downloadCsv(`tiktok-orders-selected-${Date.now()}.csv`, headers, csvRows);
    showToast(t('orderList.exported', { count: csvRows.length }), 'success');
  };

  const onBatchDelete = async () => {
    if (!activeShopId || selectedKeys.size === 0) {
      showToast(t('orderList.pickToDelete'), 'warning');
      return;
    }
    const orderNos = selectedRows
      .map((row) => pickField(row, 'orderNo', 'orderNumber'))
      .filter((no) => no != null && String(no).trim() !== '')
      .map((no) => String(no).trim());
    const ids = selectedRows
      .map((row) => Number(row?.id ?? pickField(row, 'id')))
      .filter((n) => Number.isFinite(n) && n > 0);
    if (orderNos.length === 0 && ids.length === 0) {
      showToast(t('orderList.missingOrderNo'), 'error');
      return;
    }
    const countHint = orderNos.length || ids.length;
    const ok = await showTiktokConfirm({
      title: t('orderList.batchDelete'),
      message: t('orderList.batchDeleteMsg', { count: countHint, platform: 'TikTok' }),
      tone: 'danger',
      confirmText: t('orderList.delete'),
      cancelText: t('shop.cancel'),
    });
    if (!ok) return;
    setDeleting(true);
    try {
      const res = await tiktokPlatformAPI.batchDeleteTiktokOrders(activeShopId, {
        orderNos,
        ids,
      });
      const deleted = Number(res?.deleted);
      const count = Number.isFinite(deleted) && deleted >= 0 ? deleted : countHint;
      if (count > 0) {
        showToast(t('orderList.deletedCount', { count }), 'success');
      } else {
        showToast(t('orderList.noneDeleted'), 'warning');
      }
      setSelectedKeys(new Set());
      selectedRowsRef.current.clear();
      load();
    } catch (e) {
      showToast(e?.message || t('orderList.batchDeleteFailed'), 'error');
    } finally {
      setDeleting(false);
    }
  };

  const orderIdLabel = t(ORDER_ID_OPTIONS.find((o) => o.value === filterDraft.orderIdType)?.labelKey || 'orderList.orderNo');

  return (
    <>
      <TiktokSyncProgressSlot kinds={['orders']} />
      <ProductFilterCard
        toolbar={(
          <TiktokOpsListToolbar
            selectedCount={selectedKeys.size}
            busy={syncing || loading || exporting || deleting}
            syncKinds={['orders']}
            onSync={() => {
              if (!activeShopId) {
                showToast(t('orderList.pickAuthorized', { platform: 'TikTok' }), 'warning');
                return;
              }
              setSyncRangeOpen(true);
            }}
            syncing={syncing}
            syncCooldownSeconds={syncCooldown}
            syncDisabled={shopLoading || !activeShopId || syncing}
            lastSyncedLabel={latestSyncedLabel}
            syncHint={t('orderList.syncHint', { platform: 'TikTok' })}
            exportLabel={opsExportLabel(exporting)}
            exportDisabled={exporting || !activeShopId}
            exportMenu={buildOpsExportMenu({
              exporting,
              filteredCount: total,
              pageCount: rows.length,
              selectedCount: selectedKeys.size,
              onExportFiltered: exportFiltered,
              onExportPage: exportPage,
              onExportSelected: exportSelected,
            })}
            onBatchDelete={onBatchDelete}
            deleteDisabled={!activeShopId || deleting}
          />
        )}
      >
        <div className="tko-filter-bar">
          <div className="tko-filter-bar__fields">
            <div className="tko-field tko-field--inline tko-field--id-combo tko-field--batch tko-field--w300">
              <select
                className="tko-select tko-select--compact tko-select--order-type"
                value={filterDraft.orderIdType}
                disabled={!activeShopId}
                aria-label={t('orderList.idTypeAria')}
                onChange={(e) => updateField('orderIdType', e.target.value)}
              >
                {ORDER_ID_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>{t(opt.labelKey)}</option>
                ))}
              </select>
              <input
                className="tko-input"
                value={filterDraft.orderIdValue}
                placeholder={t('orderList.idPlaceholder', { type: orderIdLabel })}
                disabled={!activeShopId}
                onChange={(e) => updateField('orderIdValue', e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && onQuery()}
              />
              <button
                type="button"
                className="tko-batch-add-btn"
                aria-label={t('orderList.batchInputId', { type: orderIdLabel })}
                title={t('orderList.batchQuery')}
                disabled={!activeShopId}
                onClick={() => {
                  setBatchModalField('orderIdValue');
                  setBatchModalOpen(true);
                }}
              >
                <Plus size={16} strokeWidth={2.25} aria-hidden="true" />
              </button>
            </div>
            <div className="tko-field tko-field--inline tko-field--id-combo tko-field--batch tko-field--w300">
              <span className="tko-field__label">{t('tiktokCatalog.productId')}</span>
              <input
                className="tko-input"
                value={filterDraft.skc}
                placeholder={t('orderList.productIdPlaceholder', { defaultValue: '产品 ID，支持逗号批量' })}
                disabled={!activeShopId}
                onChange={(e) => updateField('skc', e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && onQuery()}
              />
              <button
                type="button"
                className="tko-batch-add-btn"
                aria-label={t('tiktokCatalog.batchQueryProductId')}
                title={t('orderList.batchQuery')}
                disabled={!activeShopId}
                onClick={() => {
                  setBatchModalField('skc');
                  setBatchModalOpen(true);
                }}
              >
                <Plus size={16} strokeWidth={2.25} aria-hidden="true" />
              </button>
            </div>
            <TiktokDateRangeFilter
              showTimeType={false}
              startDate={filterDraft.startTime}
              endDate={filterDraft.endTime}
              disabled={!activeShopId}
              onStartDateChange={(v) => updateField('startTime', pickDatePart(v))}
              onEndDateChange={(v) => updateField('endTime', pickDatePart(v))}
            />
          </div>
          <div className="tko-filter-bar__actions">
            <button type="button" className="tko-btn tko-btn--secondary tko-btn--sm" onClick={onReset} disabled={!activeShopId}>
              {t('orderList.reset')}
            </button>
            <button type="button" className="tko-btn tko-btn--primary tko-btn--sm" onClick={onQuery} disabled={!activeShopId || loading}>
              {t('orderList.query')}
            </button>
          </div>
        </div>
        {error ? <div className="tko-ops-error" role="alert">{error}</div> : null}
      </ProductFilterCard>

      <BatchQueryModal
        open={batchModalOpen}
        title={batchModalField === 'skc' ? t('tiktokCatalog.batchQueryProductId') : t('orderList.batchInputId', { type: orderIdLabel })}
        initialValue={batchModalField ? filterDraft[batchModalField] : ''}
        placeholder={batchModalField === 'skc'
          ? t('tiktokCatalog.batchQueryCode')
          : t('orderList.idLinePh', { type: orderIdLabel })}
        onConfirm={(tokens) => {
          if (batchModalField) {
            updateField(batchModalField, joinBatchTokens(tokens));
          }
          setBatchModalOpen(false);
          setBatchModalField(null);
        }}
        onClose={() => {
          setBatchModalOpen(false);
          setBatchModalField(null);
        }}
      />

      <TiktokSyncRangeModal
        open={syncRangeOpen}
        title={t('orderList.syncOrders')}
        defaultHours={24 * 7}
        presets={[
          { label: t('orderList.last24h'), hours: 24 },
          { label: t('orderList.last7d'), hours: 24 * 7 },
          { label: t('orderList.last14d'), hours: 24 * 14 },
          { label: t('orderList.last30d'), hours: 24 * 30 },
          { label: t('orderList.last90d'), hours: 24 * 90 },
        ]}
        confirming={syncing}
        onConfirm={async (range) => {
          pendingSyncRangeRef.current = range;
          const ok = await guardedSync();
          if (ok !== false) setSyncRangeOpen(false);
        }}
        onClose={() => {
          if (!syncing) setSyncRangeOpen(false);
        }}
        onClose={() => setSyncRangeOpen(false)}
      />

      <ProductListCard
        loading={Boolean(activeShopId) && loading}
        loadingLabel={t('orderList.querying')}
        tabs={listTabs}
      >
        <div className="tko-drafts__table-wrap tko-ops-table-wrap">
          <table className="tko-publish__table tko-drafts__table tko-ops-table tko-ops-table--order">
            <colgroup>
              <col className="tko-ops-col tko-ops-col--check tiktok-product-col--check" />
              <col className="tko-ops-col tko-ops-col--product" />
              <col className="tko-ops-col tko-ops-col--share" />
              <col className="tko-ops-col tko-ops-col--logistics" />
              <col className="tko-ops-col tko-ops-col--share" />
              <col className="tko-ops-col tko-ops-col--date" />
              <col className="tko-ops-col tko-ops-col--price" />
              <col className="tko-ops-col tko-ops-col--ops" />
            </colgroup>
            <thead>
              <tr>
                <th
                  scope="col"
                  className="tko-ops-th tko-ops-th--check tiktok-product-cell--spu-check"
                  onClick={() => {
                    if (!activeShopId || loading || selectableKeys.length === 0) return;
                    toggleAll();
                  }}
                >
                  <input
                    type="checkbox"
                    aria-label={t('orderList.selectAll')}
                    disabled={!activeShopId || loading || selectableKeys.length === 0}
                    checked={allSelected}
                    onClick={(e) => e.stopPropagation()}
                    onChange={toggleAll}
                  />
                </th>
                <th scope="col">{t('orderList.productInfo')}</th>
                <th scope="col">{t('orderList.orderInfo')}</th>
                <th scope="col">{t('orderList.logisticsInfo')}</th>
                <th scope="col">{t('orderList.status')}</th>
                <th scope="col">{t('orderList.date')}</th>
                <th scope="col">{t('orderList.priceInfo')}</th>
                <th scope="col" className="tko-ops-th--ops">{t('orderList.actions')}</th>
              </tr>
            </thead>
            <tbody>
              {!activeShopId || loading || rows.length === 0 ? (
                <tr>
                  <td colSpan={8} className="tko-certs-empty-cell">
                    <div className="tko-certs-empty">
                      <p className="tko-certs-empty__title">{emptyTitle}</p>
                      {emptyDesc ? <p className="tko-certs-empty__desc">{emptyDesc}</p> : null}
                    </div>
                  </td>
                </tr>
              ) : (
                rows.flatMap((row, index) => {
                  const key = rowKey(row, index);
                  const no = pickField(row, 'orderNo', 'orderNumber');
                  const statusCode = pickField(row, 'orderStatus');
                  const product = listOrderProductSummary(row);
                  const orderInfo = listOrderInfoRows(row);
                  const logistics = listLogisticsSummary(row);
                  const dates = listDateRows(row);
                  const prices = listPrimaryPrices(row);
                  const trackOpen = trackPanel?.key === key;

                  const main = (
                    <tr key={key}>
                      <td
                        className="tko-ops-td tko-ops-td--check tiktok-product-cell--spu-check"
                        onClick={() => toggleOne(key, row)}
                      >
                        <input
                          type="checkbox"
                          aria-label={t('orderList.selectRow', { title: no || key })}
                          checked={selectedKeys.has(key)}
                          onClick={(e) => e.stopPropagation()}
                          onChange={() => toggleOne(key, row)}
                        />
                      </td>
                      <td className="tko-ops-td tko-ops-td--product">
                        {product.title || product.imageUrl || product.productId || product.merchantSku || product.incomeText ? (
                          <div className="tko-return-product">
                            {product.imageUrl ? (
                              <TiktokProductThumb
                                imageUrl={product.imageUrl}
                                className="tko-return-product__img"
                                placeholderClassName="tko-return-product__img tko-return-product__img--empty"
                                alt={product.title || t('orderList.productAlt')}
                              />
                            ) : (
                              <div className="tko-return-product__img tko-return-product__img--empty" />
                            )}
                            <div className="tko-return-product__meta">
                              <HoverCopyField
                                value={product.title || ''}
                                className="tko-return-product__title"
                                popoverCopy
                              />
                              <HoverCopyField
                                label={t('tiktokCatalog.productId')}
                                value={product.productId || ''}
                                className="tko-return-product__sku"
                              />
                              <HoverCopyField
                                label={t('orderList.merchantSku')}
                                value={product.merchantSku || ''}
                                className="tko-return-product__sku"
                              />
                              <HoverCopyField
                                label="SKU ID"
                                value={product.skuCode || ''}
                                className="tko-return-product__sku"
                              />
                              <div className="tko-return-product__refund">
                                {t('orderList.estIncomePrefix')}{product.incomeText || '—'}
                              </div>
                            </div>
                          </div>
                        ) : (
                          <span className="tko-return-product__empty">{t('orderList.noProduct')}</span>
                        )}
                      </td>
                      <td className="tko-ops-td tko-ops-td--orderno">
                        <HoverCopyField
                          className="tko-return-copy"
                          value={no}
                          display={no ? <strong>{no}</strong> : '—'}
                        />
                        <div className="tko-order-meta-list">
                          {orderInfo.map((item) => (
                            <div key={item.label}>
                              <span className="tko-return-times__label">{item.label}</span>
                              <span>{item.value || '—'}</span>
                            </div>
                          ))}
                        </div>
                      </td>
                      <td className="tko-ops-td tko-ops-td--logistics">
                        {logistics.items.length === 0 ? (
                          <span className="tko-return-product__empty">{t('orderList.noLogistics')}</span>
                        ) : (
                          <div className="tko-order-logistics">
                            {logistics.items.slice(0, 2).map((item, i) => (
                              <div key={`${item.packageNo}-${item.waybillNo}-${i}`} className="tko-order-logistics__item">
                                <div>{t('orderList.carrierPrefix')}{item.carrier || '—'}</div>
                                <HoverCopyField
                                  label={t('orderList.packageLabel')}
                                  value={item.packageNo || ''}
                                  className="tko-return-copy"
                                />
                                <HoverCopyField
                                  label={t('orderList.waybillLabel')}
                                  value={item.waybillNo || ''}
                                  className="tko-return-copy"
                                />
                                {item.latestNode ? <div className="tko-order-logistics__node">{item.latestNode}</div> : null}
                              </div>
                            ))}
                            {logistics.items.length > 2 ? (
                              <div className="tko-order-logistics__more">{t('orderList.morePackages', { count: logistics.items.length })}</div>
                            ) : null}
                            <button
                              type="button"
                              className="tko-btn tko-btn--ghost tko-btn--sm"
                              title={t('orderList.viewOrderTrack')}
                              onClick={() => (trackOpen ? setTrackPanel(null) : openTrack(row, 0))}
                            >
                              {trackOpen ? t('orderList.hideTrack') : t('orderList.viewTrack')}
                            </button>
                          </div>
                        )}
                      </td>
                      <td>
                        <span className={`tko-records__status tko-records__status--${statusModifier(statusCode)}`}>
                          {statusText(statusCode) || '—'}
                        </span>
                      </td>
                      <td className="tko-ops-td tko-ops-td--times">
                        <div className="tko-return-times">
                          {dates.map((d) => (
                            <div key={d.label}>
                              <span className="tko-return-times__label">{d.label}</span>
                              <span>{d.value || '—'}</span>
                            </div>
                          ))}
                        </div>
                      </td>
                      <td className="tko-ops-td tko-ops-td--price">
                        <div className="tko-return-times">
                          {(prices.primary.length ? prices.primary : [{ label: t('orderList.fee'), value: '—' }]).map((p) => (
                            <div key={p.label}>
                              <span className="tko-return-times__label">{p.label}</span>
                              <span>{p.value || '—'}</span>
                            </div>
                          ))}
                          {prices.extra.length ? (
                            <details className="tko-order-price-more">
                              <summary>{t('orderList.moreFees')}</summary>
                              {prices.extra.map((p) => (
                                <div key={p.label}>
                                  <span className="tko-return-times__label">{p.label}</span>
                                  <span>{p.value}</span>
                                </div>
                              ))}
                            </details>
                          ) : null}
                        </div>
                      </td>
                      <td className="tko-ops-td tko-ops-td--ops tko-certs-td tko-certs-td--ops">
                        <div className="tko-drafts__actions">
                          {(() => {
                            const actions = tiktokOrderActions(row);
                            const busy = processingNo === no;
                            return (
                              <>
                                {actions.ship ? (
                                  <button
                                    type="button"
                                    className="tko-btn tko-btn--primary tko-btn--sm"
                                    disabled={!no || busy}
                                    onClick={() => handleProcess(row, no)}
                                  >
                                    {busy ? fixedUi("发货中") : fixedUi("安排发货")}
                                  </button>
                                ) : null}
                                {actions.uploadWaybill ? (
                                  <button
                                    type="button"
                                    className="tko-btn tko-btn--primary tko-btn--sm"
                                    disabled={!no || busy}
                                    onClick={() => openWaybill(row, no)}
                                  >
                                    {fixedUi("上传运单")}
                                  </button>
                                ) : null}
                                {actions.printLabel ? (
                                  <button
                                    type="button"
                                    className="tko-btn tko-btn--primary tko-btn--sm"
                                    disabled={!no || busy}
                                    onClick={() => printLabel(row, no)}
                                  >
                                    {busy ? fixedUi("获取中") : fixedUi("打印面单")}
                                  </button>
                                ) : null}
                                {actions.track ? (
                                  <button
                                    type="button"
                                    className="tko-btn tko-btn--secondary tko-btn--sm"
                                    disabled={!no}
                                    onClick={() => (trackOpen ? setTrackPanel(null) : openTrack(row, 0))}
                                  >
                                    {trackOpen ? t('orderList.hideTrack') : fixedUi("查看轨迹")}
                                  </button>
                                ) : null}
                              </>
                            );
                          })()}
                          <button
                            type="button"
                            className="tko-btn tko-btn--secondary tko-btn--sm"
                            disabled={!no}
                            onClick={() => openDetail(no)}
                          >
                            {t('orderList.detail')}
                          </button>
                        </div>
                      </td>
                    </tr>
                  );

                  if (!trackOpen) return [main];

                  const item = trackPanel.items[trackPanel.packageIdx] || trackPanel.items[0];
                  const snap = trackPanel.liveTrack
                    ? { track: trackPanel.liveTrack, packageNo: item?.packageNo, waybillNo: item?.waybillNo }
                    : item?.trackSnap;
                  const tracks = trackInfoList(snap?.track || snap);

                  return [
                    main,
                    <tr key={`${key}-track`} className="tko-table__detail-row">
                      <td colSpan={8}>
                        <div className="tko-order-track">
                          <div className="tko-order-track__toolbar">
                            <strong>{t('orderList.orderTrack')}</strong>
                            {trackPanel.items.length > 1 ? (
                              <label className="tko-field tko-field--inline">
                                <span className="tko-field__label">{t('orderList.packageLabel')}</span>
                                <select
                                  className="tko-select"
                                  value={String(trackPanel.packageIdx ?? 0)}
                                  onChange={(e) => openTrack(row, Number(e.target.value))}
                                >
                                  {trackPanel.items.map((ref, i) => (
                                    <option key={`${ref.packageNo}-${ref.waybillNo}-${i}`} value={i}>
                                      {ref.packageNo || ref.waybillNo || t('orderList.packageN', { index: i + 1 })}
                                    </option>
                                  ))}
                                </select>
                              </label>
                            ) : null}
                            <button
                              type="button"
                              className="tko-btn tko-btn--secondary tko-btn--sm"
                              disabled={trackPanel.liveLoading}
                              onClick={() => refreshLiveTrack(row)}
                            >
                              {trackPanel.liveLoading ? t('orderList.refreshingTrack') : t('orderList.refreshTrack')}
                            </button>
                          </div>
                          {trackPanel.liveError ? (
                            <div className="tko-order-track__empty">{trackPanel.liveError}</div>
                          ) : trackPanel.liveLoading ? (
                            <div className="tko-order-track__empty">{t('orderList.loadingTrack')}</div>
                          ) : tracks.length === 0 ? (
                            <div className="tko-order-track__empty">
                              {/9999002|数据不存在/i.test(String(item?.error || ''))
                                ? t('orderList.noTrackData')
                                : (item?.error
                                  ? t('orderList.noTrack')
                                  : t('orderList.noTrackNode', { platform: 'TikTok' }))}
                            </div>
                          ) : (
                            tracks.map((info, infoIdx) => (
                              <div key={`${pickField(info, 'waybillNo') || infoIdx}`} className="tko-order-track__block">
                                <div className="tko-order-track__meta">
                                  <span>{t('orderList.waybillPrefix')}{pickField(info, 'waybillNo') || item?.waybillNo || '—'}</span>
                                  <span>{t('orderList.carrierPrefix')}{pickField(info, 'carrier') || item?.carrier || '—'}</span>
                                  <span>{t('orderList.typePrefix')}{pickField(info, 'waybillType') || '—'}</span>
                                </div>
                                <ol className="tko-order-track__timeline">
                                  {(Array.isArray(info.tracking) ? info.tracking : []).map((node, nodeIdx) => (
                                    <li key={`${nodeIdx}-${pickField(node, 'updateTimeMillis')}`}>
                                      <div className="tko-order-track__time">
                                        {formatTrackTime(pickField(node, 'updateTimeMillis'))}
                                      </div>
                                      <div className="tko-order-track__node">
                                        <strong>{pickField(node, 'nodeCodeName', 'nodeCode') || t('orderList.node')}</strong>
                                        <span>{pickField(node, 'description') || '—'}</span>
                                      </div>
                                    </li>
                                  ))}
                                </ol>
                              </div>
                            ))
                          )}
                        </div>
                      </td>
                    </tr>,
                  ];
                })
              )}
            </tbody>
          </table>
        </div>

        <TiktokOpsPager
          page={page}
          pageSize={pageSize}
          total={total}
          loading={loading}
          disabled={!activeShopId}
          pageItemCount={rows.length}
          onPageChange={setPage}
          onPageSizeChange={(size) => {
            setPageSize(size);
            setPage(1);
          }}
          ariaLabel={t('orderList.pager')}
        />
      </ProductListCard>
      {waybillModal ? (
        <div className="tko-waybill-modal" role="presentation" onClick={() => !waybillModal.submitting && setWaybillModal(null)}>
          <div className="tko-waybill-modal__backdrop" aria-hidden="true" />
          <div
            className="tko-waybill-modal__panel"
            role="dialog"
            aria-modal="true"
            aria-labelledby="tko-order-waybill-title"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="tko-waybill-modal__head">
              <div>
                <h2 id="tko-order-waybill-title">{fixedUi("上传运单")}</h2>
                <p className="tko-waybill-modal__order">{waybillModal.orderNo}</p>
              </div>
              <button
                type="button"
                className="tko-waybill-modal__close"
                disabled={waybillModal.submitting}
                onClick={() => setWaybillModal(null)}
              >
                ×
              </button>
            </div>
            <div className="tko-waybill-modal__body">
              <label className="tko-waybill-modal__field">
                <span>{fixedUi("物流商")}</span>
                {waybillModal.providers.length ? (
                  <select
                    value={waybillModal.providerId}
                    onChange={(e) => setWaybillModal((prev) => (prev ? { ...prev, providerId: e.target.value } : prev))}
                  >
                    <option value="">{fixedUi("请选择")}</option>
                    {waybillModal.providers.map((item) => (
                      <option key={item.id} value={item.id}>{item.name || item.id}</option>
                    ))}
                  </select>
                ) : (
                  <input
                    placeholder={waybillModal.loadingProviders ? fixedUi('正在加载物流商') : fixedUi("物流商 ID")}
                    value={waybillModal.providerId}
                    onChange={(e) => setWaybillModal((prev) => (prev ? { ...prev, providerId: e.target.value } : prev))}
                  />
                )}
              </label>
              <label className="tko-waybill-modal__field">
                <span>{fixedUi("运单号")}</span>
                <input
                  value={waybillModal.trackingNo}
                  placeholder={fixedUi("请输入运单号")}
                  onChange={(e) => setWaybillModal((prev) => (prev ? { ...prev, trackingNo: e.target.value } : prev))}
                />
              </label>
            </div>
            <div className="tko-waybill-modal__foot">
              <button
                type="button"
                className="tko-btn tko-btn--secondary tko-btn--sm"
                disabled={waybillModal.submitting}
                onClick={() => setWaybillModal(null)}
              >
                {fixedUi("取消")}
              </button>
              <button
                type="button"
                className="tko-btn tko-btn--primary tko-btn--sm"
                disabled={waybillModal.submitting}
                onClick={submitWaybill}
              >
                {waybillModal.submitting ? fixedUi('提交中') : fixedUi('提交运单')}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </>
  );
}
